import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Loader2, KeyRound, History } from "lucide-react";

/**
 * AdminUserEditor — Modale plein écran (mobile-first) permettant à
 * l'admin de modifier TOUS les champs d'un profil utilisateur Price
 * (passager OU covoitureur). Utilise l'endpoint
 * `PATCH /api/admin/price/users/{user_id}` — chaque champ est optionnel,
 * seuls les diffs sont envoyés. Historique complet accessible en 1 clic.
 *
 * Props :
 *   userId        — id du user cible (identique à users.id)
 *   initialRole   — 'client' | 'driver' (pré-chargement UI)
 *   onClose()     — appelé quand l'admin ferme la modale
 *   onSaved()     — appelé après un save réussi (parent recharge la liste)
 */
export default function AdminUserEditor({ userId, initialRole, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [role, setRole] = useState(initialRole || "client");
  const [form, setForm] = useState({});
  const [newPassword, setNewPassword] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [notifyUser, setNotifyUser] = useState(true);
  const [adminNote, setAdminNote] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/price/users/${userId}`);
        if (cancelled) return;
        setSnapshot(data);
        setRole(data.role || initialRole || "client");
        const u = data.user || {};
        const p = data.profile || {};
        setForm({
          email: u.email || "",
          role: data.role || "client",
          email_verified: !!u.email_verified,
          suspended: (u.status || "") === "suspended",
          // client
          full_name: p.full_name || "",
          preferred_language: p.preferred_language || "fr",
          // driver
          first_name: p.first_name || "",
          last_name: p.last_name || "",
          address: p.address || "",
          siret: p.siret || "",
          license_plate: p.license_plate || "",
          vehicle_brand_model: p.vehicle_brand_model || "",
          vehicle_type: p.vehicle_type || "",
          price_vehicle_kind: p.price_vehicle_kind || "",
          region: p.region || "paca",
          wallet_balance_eur: typeof p.wallet_balance_eur === "number" ? p.wallet_balance_eur : 0,
          cagnotte_balance_eur: typeof p.cagnotte_balance_eur === "number" ? p.cagnotte_balance_eur : 0,
          // Commission personnalisée : null = pas d'override (taux global).
          // "" en UI signifie aussi "pas d'override".
          commission_pct_override: (p.commission_pct_override === null || p.commission_pct_override === undefined) ? "" : p.commission_pct_override,
          // shared
          phone: p.phone || "",
          referral_code: p.referral_code || "",
          identity_status: p.identity_status || "unverified",
          identity_rejection_reason: p.identity_rejection_reason || "",
        });
      } catch (err) {
        toast.error(formatApiError(err));
        onClose && onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, initialRole, onClose]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadHistory = async () => {
    setHistoryOpen(true);
    try {
      const { data } = await api.get(`/admin/price/users/${userId}/edit-history?limit=30`);
      setHistory(data?.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const dirty = useMemo(() => {
    if (!snapshot) return {};
    const u = snapshot.user || {};
    const p = snapshot.profile || {};
    const diff = {};
    // Auth
    if ((form.email || "").trim().toLowerCase() !== (u.email || "").toLowerCase())
      diff.email = (form.email || "").trim().toLowerCase();
    if (form.role !== (snapshot.role || "client")) diff.role = form.role;
    if (!!form.email_verified !== !!u.email_verified) diff.email_verified = !!form.email_verified;
    const wasSuspended = (u.status || "") === "suspended";
    if (!!form.suspended !== wasSuspended) diff.suspended = !!form.suspended;
    // Password (envoyé seulement si champ rempli)
    if (newPassword.trim().length >= 6) diff.password = newPassword.trim();
    // Fields communs
    if ((form.phone || "") !== (p.phone || "")) diff.phone = form.phone || "";
    if ((form.referral_code || "") !== (p.referral_code || "")) diff.referral_code = (form.referral_code || "").toUpperCase();
    if ((form.identity_status || "") !== (p.identity_status || "unverified")) diff.identity_status = form.identity_status;
    if (form.identity_status === "rejected" && (form.identity_rejection_reason || "") !== (p.identity_rejection_reason || ""))
      diff.identity_rejection_reason = form.identity_rejection_reason || "";
    // Fields client
    if (role === "client") {
      if ((form.full_name || "") !== (p.full_name || "")) diff.full_name = form.full_name || "";
      if ((form.preferred_language || "") !== (p.preferred_language || "")) diff.preferred_language = form.preferred_language;
    }
    // Fields driver
    if (role === "driver") {
      const driverKeys = ["first_name", "last_name", "address", "siret", "license_plate",
        "vehicle_brand_model", "vehicle_type", "price_vehicle_kind", "region"];
      for (const k of driverKeys) {
        if ((form[k] || "") !== (p[k] || "")) diff[k] = form[k] || "";
      }
      const oldBal = typeof p.wallet_balance_eur === "number" ? p.wallet_balance_eur : 0;
      const newBal = Number(form.wallet_balance_eur);
      if (!isNaN(newBal) && Math.abs(newBal - oldBal) > 0.001) diff.wallet_balance_eur = newBal;
      const oldCagn = typeof p.cagnotte_balance_eur === "number" ? p.cagnotte_balance_eur : 0;
      const newCagn = Number(form.cagnotte_balance_eur);
      if (!isNaN(newCagn) && Math.abs(newCagn - oldCagn) > 0.001) diff.cagnotte_balance_eur = newCagn;
      // Commission override : "" = pas d'override → envoie -1 pour reset,
      // sinon nombre 0-100. On ne modifie que si la valeur change.
      const oldOverride = (p.commission_pct_override === null || p.commission_pct_override === undefined) ? "" : p.commission_pct_override;
      const newOverrideRaw = form.commission_pct_override;
      const newOverride = (newOverrideRaw === "" || newOverrideRaw === null || newOverrideRaw === undefined) ? "" : Number(newOverrideRaw);
      if (String(oldOverride) !== String(newOverride)) {
        // Backend attend -1 pour effacer, sinon un float 0-100.
        diff.commission_pct_override = newOverride === "" ? -1 : newOverride;
      }
    }
    return diff;
  }, [form, snapshot, newPassword, role]);

  const changeCount = Object.keys(dirty).length;

  const handleSave = async () => {
    if (saving) return;
    if (changeCount === 0) {
      toast.info("Aucune modification à enregistrer");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...dirty, notify_user: notifyUser, admin_note: adminNote || "" };
      const { data } = await api.patch(`/admin/price/users/${userId}`, payload);
      toast.success(`✅ ${data?.changes?.length || 0} modification(s) enregistrée(s)`);
      setNewPassword("");
      setAdminNote("");
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      data-testid="admin-user-editor"
    >
      <div
        className="relative w-full sm:max-w-2xl max-h-[95vh] overflow-hidden rounded-t-3xl sm:rounded-3xl bg-white street-border street-shadow flex flex-col"
      >
        {/* Header sticky */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 bg-primary/10 border-b-4 border-foreground">
          <div className="min-w-0">
            <div className="text-[11px] font-black uppercase tracking-widest text-primary">
              Édition profil {role === "driver" ? "covoitureur" : "passager"}
            </div>
            <div className="truncate font-[Outfit] text-[15px] font-black leading-tight">
              {snapshot?.user?.email || (loading ? "Chargement…" : "—")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadHistory}
              data-testid="user-editor-history-btn"
              title="Historique des modifications"
              className="inline-flex items-center gap-1 rounded-lg bg-neutral-900 px-2.5 py-1.5 text-[11px] font-black text-white street-border-thin street-press"
            >
              <History className="h-3.5 w-3.5" /> Historique
            </button>
            <button
              type="button"
              onClick={() => onClose && onClose()}
              data-testid="user-editor-close-btn"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white street-border-thin street-press"
              title="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body scrollable */}
        {loading ? (
          <div className="flex-1 grid place-items-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
            {/* --- Auth --- */}
            <Section title="🔐 Authentification">
              <Field label="Email" testid="edit-email">
                <input
                  type="email"
                  value={form.email || ""}
                  onChange={(e) => set("email", e.target.value)}
                  className="input"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Type de compte">
                  <select
                    value={form.role || "client"}
                    onChange={(e) => { set("role", e.target.value); setRole(e.target.value); }}
                    className="input"
                    data-testid="edit-role"
                  >
                    <option value="client">Passager</option>
                    <option value="driver">Covoitureur</option>
                  </select>
                </Field>
                <Field label="Email vérifié">
                  <Toggle
                    checked={!!form.email_verified}
                    onChange={(v) => set("email_verified", v)}
                    testid="edit-email-verified"
                  />
                </Field>
              </div>
              <Field label="Compte suspendu">
                <Toggle
                  checked={!!form.suspended}
                  onChange={(v) => set("suspended", v)}
                  testid="edit-suspended"
                />
              </Field>
              <Field label="🔑 Réinitialiser le mot de passe" testid="edit-password">
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Laisser vide pour ne rien changer"
                  className="input"
                />
                <div className="text-[11px] text-foreground/60 mt-1">6 caractères minimum. L&apos;utilisateur devra se reconnecter.</div>
              </Field>
            </Section>

            {/* --- Profil --- */}
            <Section title={role === "driver" ? "👤 Identité covoitureur" : "👤 Identité passager"}>
              {role === "client" ? (
                <>
                  <Field label="Nom complet">
                    <input
                      value={form.full_name || ""}
                      onChange={(e) => set("full_name", e.target.value)}
                      className="input"
                      data-testid="edit-full-name"
                    />
                  </Field>
                  <Field label="Langue préférée">
                    <select
                      value={form.preferred_language || "fr"}
                      onChange={(e) => set("preferred_language", e.target.value)}
                      className="input"
                      data-testid="edit-language"
                    >
                      <option value="fr">Français</option>
                      <option value="en">Anglais</option>
                      <option value="ar">Arabe</option>
                    </select>
                  </Field>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Prénom">
                    <input value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value)} className="input" data-testid="edit-first-name" />
                  </Field>
                  <Field label="Nom">
                    <input value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)} className="input" data-testid="edit-last-name" />
                  </Field>
                </div>
              )}
              <Field label="Téléphone">
                <input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="input" placeholder="+33 6 …" data-testid="edit-phone" />
              </Field>
              {role === "driver" && (
                <Field label="Adresse">
                  <input value={form.address || ""} onChange={(e) => set("address", e.target.value)} className="input" data-testid="edit-address" />
                </Field>
              )}
              <Field label="Code de parrainage">
                <input
                  value={form.referral_code || ""}
                  onChange={(e) => set("referral_code", e.target.value.toUpperCase())}
                  className="input font-mono"
                  placeholder="ex: PRICE42"
                  data-testid="edit-referral-code"
                />
              </Field>
            </Section>

            {/* --- Véhicule (driver only) --- */}
            {role === "driver" && (
              <Section title="🚗 Véhicule">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Marque / modèle">
                    <input value={form.vehicle_brand_model || ""} onChange={(e) => set("vehicle_brand_model", e.target.value)} className="input" data-testid="edit-vehicle-brand" />
                  </Field>
                  <Field label="Plaque">
                    <input value={form.license_plate || ""} onChange={(e) => set("license_plate", e.target.value.toUpperCase())} className="input font-mono" data-testid="edit-plate" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Type de véhicule">
                    <input value={form.vehicle_type || ""} onChange={(e) => set("vehicle_type", e.target.value)} className="input" placeholder="berline, SUV…" data-testid="edit-vehicle-type" />
                  </Field>
                  <Field label="Kind Price">
                    <select value={form.price_vehicle_kind || ""} onChange={(e) => set("price_vehicle_kind", e.target.value)} className="input" data-testid="edit-price-kind">
                      <option value="">—</option>
                      <option value="car">Voiture</option>
                      <option value="moto">Moto / scooter</option>
                    </select>
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="SIRET">
                    <input value={form.siret || ""} onChange={(e) => set("siret", e.target.value)} className="input font-mono" data-testid="edit-siret" />
                  </Field>
                  <Field label="Région">
                    <select value={form.region || "paca"} onChange={(e) => set("region", e.target.value)} className="input" data-testid="edit-region">
                      <option value="paca">PACA</option>
                      <option value="idf">Île-de-France</option>
                      <option value="occitanie">Occitanie</option>
                      <option value="aura">AURA</option>
                      <option value="autre">Autre</option>
                    </select>
                  </Field>
                </div>
              </Section>
            )}

            {/* --- Portefeuille (driver only) --- */}
            {role === "driver" && (
              <Section title="💰 Cagnotte & Portefeuille">
                <Field label="🐷 Cagnotte (€) — non retirable">
                  <input
                    type="number"
                    step="0.01"
                    value={form.cagnotte_balance_eur ?? 0}
                    onChange={(e) => set("cagnotte_balance_eur", e.target.value)}
                    className="input font-mono"
                    data-testid="edit-cagnotte-balance"
                  />
                  <div className="text-[11px] text-foreground/60 mt-1">
                    Sert à payer les commissions cash. Alimentée par recharges Stripe, parrainages et ajustements admin.
                  </div>
                </Field>
                <Field label="💳 Portefeuille (€) — retirable">
                  <input
                    type="number"
                    step="0.01"
                    value={form.wallet_balance_eur ?? 0}
                    onChange={(e) => set("wallet_balance_eur", e.target.value)}
                    className="input font-mono"
                    data-testid="edit-wallet-balance"
                  />
                  <div className="text-[11px] text-foreground/60 mt-1">
                    Gains cartes accumulés. Retirable vers compte bancaire.
                  </div>
                </Field>
                <Field label="Taux de commission personnalisé (%)">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      max="100"
                      value={form.commission_pct_override}
                      onChange={(e) => set("commission_pct_override", e.target.value)}
                      className="input font-mono flex-1"
                      placeholder="Laisser vide = taux global"
                      data-testid="edit-commission-override"
                    />
                    <button
                      type="button"
                      onClick={() => set("commission_pct_override", "")}
                      className="rounded-lg bg-neutral-200 px-3 py-2 text-[11px] font-black text-neutral-700 street-border-thin street-press"
                      title="Repasser au taux global"
                      data-testid="edit-commission-reset"
                    >
                      Reset
                    </button>
                  </div>
                  <div className="text-[11px] text-foreground/60 mt-1">
                    <b>0</b> = 0 % (exempté, courses gratuites pour Price). <b>Vide</b> = utilise le taux global de la plateforme.
                  </div>
                </Field>
              </Section>
            )}

            {/* --- KYC --- */}
            <Section title="🪪 Vérification d'identité (KYC)">
              <Field label="Statut">
                <select
                  value={form.identity_status || "unverified"}
                  onChange={(e) => set("identity_status", e.target.value)}
                  className="input"
                  data-testid="edit-kyc-status"
                >
                  <option value="unverified">Non vérifié</option>
                  <option value="pending">En attente</option>
                  <option value="verified">Vérifié ✅</option>
                  <option value="rejected">Refusé ❌</option>
                </select>
              </Field>
              {form.identity_status === "rejected" && (
                <Field label="Motif de refus">
                  <input
                    value={form.identity_rejection_reason || ""}
                    onChange={(e) => set("identity_rejection_reason", e.target.value)}
                    className="input"
                    placeholder="Ex: pièce illisible, expirée…"
                    data-testid="edit-kyc-reason"
                  />
                </Field>
              )}
            </Section>

            {/* --- Notes admin & notif --- */}
            <Section title="🗒️ Journal & notification">
              <Field label="Motif interne (audit log)">
                <textarea
                  rows={2}
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  className="input resize-none"
                  placeholder="Ex: correction faute de frappe, demande client…"
                  data-testid="edit-admin-note"
                />
              </Field>
              <Field label="Prévenir l'utilisateur">
                <Toggle
                  checked={notifyUser}
                  onChange={setNotifyUser}
                  testid="edit-notify-user"
                />
                <div className="text-[11px] text-foreground/60 mt-1">
                  Envoie un push OneSignal + un email récapitulatif (template Price).
                </div>
              </Field>
            </Section>
          </div>
        )}

        {/* Footer sticky */}
        {!loading && (
          <div className="flex items-center justify-between gap-2 px-4 py-3 bg-white border-t-4 border-foreground">
            <div className="text-[12px] font-bold text-foreground/70">
              {changeCount === 0 ? "Aucune modification" : `${changeCount} champ${changeCount > 1 ? "s" : ""} modifié${changeCount > 1 ? "s" : ""}`}
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || changeCount === 0}
              data-testid="user-editor-save-btn"
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-[13px] font-black text-primary-foreground street-border-thin street-press disabled:opacity-40"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Enregistrer
            </button>
          </div>
        )}

        {/* History overlay */}
        {historyOpen && (
          <div
            className="absolute inset-0 bg-white/95 backdrop-blur-sm z-10 flex flex-col"
            data-testid="user-editor-history-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b-4 border-foreground bg-neutral-900 text-white">
              <div className="font-[Outfit] text-[15px] font-black">Historique des modifications</div>
              <button
                type="button"
                onClick={() => setHistoryOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-neutral-900 street-border-thin street-press"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
              {history.length === 0 ? (
                <div className="text-center py-8 text-foreground/60 text-[13px]">Aucun historique</div>
              ) : (
                history.map((h) => (
                  <div key={h.id} className="rounded-xl bg-neutral-50 street-border-thin p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-[11px] font-black uppercase tracking-wider text-primary">{h.field}</div>
                      <div className="text-[10px] text-foreground/50">{(h.created_at || "").replace("T", " ").slice(0, 16)}</div>
                    </div>
                    <div className="mt-1 text-[12px] font-mono text-foreground/80 break-words">
                      <span className="text-red-600">{JSON.stringify(h.old_value)}</span>
                      {" → "}
                      <span className="text-emerald-700">{JSON.stringify(h.new_value)}</span>
                    </div>
                    <div className="mt-1 text-[11px] text-foreground/60">
                      par <b>{h.admin_email}</b>
                      {h.note ? ` · ${h.note}` : ""}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Local input style (uses existing street theme) */}
      <style>{`
        .input {
          width: 100%;
          border: 3px solid #1E0910;
          background: #FFFFFF;
          padding: 8px 12px;
          border-radius: 12px;
          font-family: 'Outfit', sans-serif;
          font-size: 14px;
          font-weight: 600;
          box-shadow: 3px 3px 0 0 #1E0910;
        }
        .input:focus { outline: none; box-shadow: 4px 4px 0 0 #F86A78; }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl bg-neutral-50 street-border-thin p-3 space-y-3">
      <div className="text-[12px] font-black uppercase tracking-widest text-foreground/70">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="mb-1 text-[11px] font-black uppercase tracking-wider text-foreground/60">{label}</div>
      {children}
    </label>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 street-border-thin street-press text-[12px] font-black ${
        checked ? "bg-emerald-400 text-neutral-900" : "bg-neutral-200 text-neutral-700"
      }`}
    >
      <span className={`inline-block h-3 w-3 rounded-full ${checked ? "bg-white" : "bg-neutral-500"}`} />
      {checked ? "Oui" : "Non"}
    </button>
  );
}
